import { createBrowserRouter } from "react-router-dom";
import { RedirectIfAuthenticated, RequireAuth, RequireRole } from "./auth/guards";
import { AgentShell } from "./layout/AgentShell";
import { AppShell } from "./layout/AppShell";
import { AppErrorBoundary } from "./routes/AppErrorBoundary";
import { NotFound } from "./routes/NotFound";
import { lazyScreen } from "./routes/lazyScreen";

// Chaque écran est chargé À LA DEMANDE. Sans cela, le bundle initial contient
// les 41 écrans, leurs composants et leurs dépendances (Leaflet compris) —
// l'utilisateur télécharge l'espace agent pour afficher l'écran de connexion.
// Le natif n'avait pas ce problème : l'app était installée d'un bloc.
const AuthCallbackScreen = lazyScreen(() => import("@/features/auth/screens/AuthCallbackScreen"), "AuthCallbackScreen");
const PartnerShell = lazyScreen(() => import("@/features/partners/screens/PartnerShell"), "PartnerShell");
const PartnerEntryScreen = lazyScreen(() => import("@/features/partners/screens/PartnerEntryScreen"), "PartnerEntryScreen");
const PartnerWorkspace = lazyScreen(() => import("@/features/partners/screens/PartnerWorkspace"), "PartnerWorkspace");
const PartnerProfileScreen = lazyScreen(() => import("@/features/partners/screens/PartnerProfileScreen"), "PartnerProfileScreen");
const PartnerCatalogScreen = lazyScreen(() => import("@/features/partners/screens/PartnerCatalogScreen"), "PartnerCatalogScreen");
const PartnerDeliveryScreen = lazyScreen(() => import("@/features/partners/screens/PartnerDeliveryScreen"), "PartnerDeliveryScreen");
const PartnerMembersScreen = lazyScreen(() => import("@/features/partners/screens/PartnerMembersScreen"), "PartnerMembersScreen");
const LoginScreen = lazyScreen(() => import("@/features/auth/screens/LoginScreen"), "LoginScreen");
const ProfilMenageScreen = lazyScreen(() => import("@/features/auth/screens/ProfilMenageScreen"), "ProfilMenageScreen");
const OrdersScreen = lazyScreen(() => import("@/features/orders/screens/OrdersScreen"), "OrdersScreen");
const OrderDetailScreen = lazyScreen(() => import("@/features/orders/screens/OrderDetailScreen"), "OrderDetailScreen");
const NotificationsScreen = lazyScreen(() => import("@/features/client/screens/NotificationsScreen"), "NotificationsScreen");
const ProfileScreen = lazyScreen(() => import("@/features/client/screens/ProfileScreen"), "ProfileScreen");
const WalletScreen = lazyScreen(() => import("@/features/wallet/screens/WalletScreen"), "WalletScreen");
const HomeScreen = lazyScreen(() => import("@/features/client/screens/HomeScreen"), "HomeScreen");
const ConfirmAddressScreen = lazyScreen(() => import("@/features/client/screens/ConfirmAddressScreen"), "ConfirmAddressScreen");
const SelectPaymentMethodScreen = lazyScreen(() => import("@/features/client/screens/SelectPaymentMethodScreen"), "SelectPaymentMethodScreen");
const TripLiveMapScreen = lazyScreen(() => import("@/features/client/screens/TripLiveMapScreen"), "TripLiveMapScreen");
const SearchingAgentScreen = lazyScreen(() => import("@/features/client/screens/SearchingAgentScreen"), "SearchingAgentScreen");
const ClientTripTrackingScreen = lazyScreen(() => import("@/features/client/screens/ClientTripTrackingScreen"), "ClientTripTrackingScreen");
const ClientNewTripScreen = lazyScreen(() => import("@/features/client/screens/ClientNewTripScreen"), "ClientNewTripScreen");
const ClientNewDeliveryScreen = lazyScreen(() => import("@/features/client/screens/ClientNewDeliveryScreen"), "ClientNewDeliveryScreen");
const TrackingScreen = lazyScreen(() => import("@/features/client/screens/TrackingScreen"), "TrackingScreen");
const SuggestionsScreen = lazyScreen(() => import("@/features/client/screens/SuggestionsScreen"), "SuggestionsScreen");
const WalletRechargeScreen = lazyScreen(() => import("@/features/wallet/screens/WalletRechargeScreen"), "WalletRechargeScreen");
const WalletWithdrawScreen = lazyScreen(() => import("@/features/wallet/screens/WalletWithdrawScreen"), "WalletWithdrawScreen");
const WalletHistoryScreen = lazyScreen(() => import("@/features/wallet/screens/WalletHistoryScreen"), "WalletHistoryScreen");
const AgentHomeScreen = lazyScreen(() => import("@/features/agent/screens/AgentHomeScreen"), "AgentHomeScreen");
const AgentNotificationsScreen = lazyScreen(() => import("@/features/agent/screens/AgentNotificationsScreen"), "AgentNotificationsScreen");
const AgentGainsScreen = lazyScreen(() => import("@/features/agent/screens/AgentGainsScreen"), "AgentGainsScreen");
const AgentPayoutScreen = lazyScreen(() => import("@/features/agent/screens/AgentPayoutScreen"), "AgentPayoutScreen");
const VIPScreen = lazyScreen(() => import("@/features/client/screens/VIPScreen"), "VIPScreen");
const DiasporaScreen = lazyScreen(() => import("@/features/diaspora/screens/DiasporaScreen"), "DiasporaScreen");
const DiasporaNewOrderScreen = lazyScreen(() => import("@/features/diaspora/screens/DiasporaNewOrderScreen"), "DiasporaNewOrderScreen");

const AgentMissionScreen = lazyScreen(() => import("@/features/agent/screens/AgentMissionScreen"), "AgentMissionScreen");
const AgentChecklistScreen = lazyScreen(() => import("@/features/agent/screens/AgentChecklistScreen"), "AgentChecklistScreen");
const AgentEncaissementScreen = lazyScreen(() => import("@/features/agent/screens/AgentEncaissementScreen"), "AgentEncaissementScreen");
// Les deux écrans de course embarquent Leaflet (AgentRouteMap) — le découpage
// par route garde ces ~160 kB hors du bundle des autres écrans agent.
const AgentTripScreen = lazyScreen(() => import("@/features/agent/screens/AgentTripScreen"), "AgentTripScreen");
const AgentVerifyStopScreen = lazyScreen(() => import("@/features/agent/screens/AgentVerifyStopScreen"), "AgentVerifyStopScreen");
const AgentPickupProofScreen = lazyScreen(() => import("@/features/agent/screens/AgentPickupProofScreen"), "AgentPickupProofScreen");
const EmergencyScreen = lazyScreen(() => import("@/features/sos/screens/EmergencyScreen"), "EmergencyScreen");
const SOSBabyScreen = lazyScreen(() => import("@/features/sos/screens/SOSBabyScreen"), "SOSBabyScreen");
const SOSHealthScreen = lazyScreen(() => import("@/features/sos/screens/SOSHealthScreen"), "SOSHealthScreen");
const SOSTrackingScreen = lazyScreen(() => import("@/features/sos/screens/SOSTrackingScreen"), "SOSTrackingScreen");

// Plan de routes — remplace ClientStack / AgentStack / AuthStack.
//
// Trois changements de fond par rapport à react-navigation :
//
//   • Chaque écran a une URL partageable et bookmarkable. Les identifiants
//     passent par les segments d'URL, plus par `navigation.navigate`.
//     Les constructeurs typés vivent dans routes/paths.ts — aucun chemin
//     littéral ne doit apparaître ailleurs.
//
//   • Les deeplinks `sosmain://` deviennent de vraies URLs :
//       sosmain://orders/:orderId/tracking  →  /orders/:orderId/tracking
//       sosmain://sos                       →  /sos
//
//   • Le natif CHOISISSAIT un stack selon le rôle ; ici on REDIRIGE, parce que
//     l'utilisateur peut taper n'importe quelle URL. Cf. app/auth/guards.tsx.
//
// ⚠️ Le déploiement doit renvoyer index.html sur TOUTE route inconnue
// (fallback SPA). Sans cette règle côté serveur, un rechargement sur
// /orders/abc/tracking renvoie un 404 du serveur de fichiers, jamais l'app.

// `errorElement` sur CHAQUE branche racine, et non sur une seule route
// englobante : React Router remonte jusqu'au plus proche ancêtre qui en
// possède un. Sans ces déclarations, une exception dans n'importe quel écran
// affichait la page de secours de la bibliothèque — « Unexpected Application
// Error! », la stack trace et un message adressé au développeur.
export const router = createBrowserRouter([
  // ── Public / authentification ────────────────────────────────────────────
  {
    element: <RedirectIfAuthenticated />,
    errorElement: <AppErrorBoundary />,
    children: [
      { path: "/login", element: <LoginScreen /> },
      { path: "/auth/callback", element: <AuthCallbackScreen /> },
    ],
  },

  // ── Espace client ─────────────────────────────────────────────────────────
  {
    element: <RequireAuth />,
    errorElement: <AppErrorBoundary />,
    children: [
      {
        path: "/partenaire",
        element: <PartnerShell />,
        children: [
          { index: true, element: <PartnerEntryScreen /> },
          {
            path: ":partnerId", element: <PartnerWorkspace />,
            children: [
              { index: true, element: <PartnerProfileScreen /> },
              { path: "catalogue", element: <PartnerCatalogScreen /> },
              { path: "livraison", element: <PartnerDeliveryScreen /> },
              { path: "membres", element: <PartnerMembersScreen /> },
            ],
          },
        ],
      },
      {
        element: <RequireRole role="CLIENT" />,
        children: [
          {
            path: "/",
            element: <AppShell />,
            // Posé aussi au niveau du shell : une erreur d'écran est ainsi
            // captée AU PLUS PRÈS, sans faire disparaître la session ni
            // renvoyer l'utilisateur hors de son espace.
            errorElement: <AppErrorBoundary />,
            children: [
              { index: true, element: <HomeScreen /> },

              // Commandes
              { path: "orders", element: <OrdersScreen /> },
              { path: "orders/:orderId", element: <OrderDetailScreen /> },
              { path: "orders/:orderId/tracking", element: <TrackingScreen /> },
              { path: "orders/:orderId/searching", element: <SearchingAgentScreen /> },

              // Courses multi-arrêts
              { path: "trips/new", element: <ClientNewTripScreen /> },
              // Livraison de colis — formulaire propre, sans articles.
              { path: "deliveries/new", element: <ClientNewDeliveryScreen /> },
              { path: "trips/:tripId", element: <ClientTripTrackingScreen /> },
              { path: "trips/:tripId/map", element: <TripLiveMapScreen /> },

              // Portefeuille
              { path: "wallet", element: <WalletScreen /> },
              { path: "wallet/recharge", element: <WalletRechargeScreen /> },
              { path: "wallet/withdraw", element: <WalletWithdrawScreen /> },
              { path: "wallet/history", element: <WalletHistoryScreen /> },

              // Compte & divers
              { path: "profile", element: <ProfileScreen /> },
              // ProfilMenage EDITE le profil d'un utilisateur connecte. En natif
              // il vivait dans AuthStack, mais le callback appelle deja
              // completeOnboarding() : place sous RedirectIfAuthenticated, la
              // garde l'aurait renvoye a l'accueil sans jamais l'afficher.
              { path: "auth/profil", element: <ProfilMenageScreen /> },
              { path: "notifications", element: <NotificationsScreen /> },
              { path: "suggestions", element: <SuggestionsScreen /> },
              { path: "addresses/confirm", element: <ConfirmAddressScreen /> },
              { path: "payment-method", element: <SelectPaymentMethodScreen /> },
              { path: "vip", element: <VIPScreen /> },
              { path: "diaspora", element: <DiasporaScreen /> },
              { path: "diaspora/new", element: <DiasporaNewOrderScreen /> },

              // Urgence
              { path: "sos", element: <EmergencyScreen /> },
              { path: "sos/baby", element: <SOSBabyScreen /> },
              { path: "sos/health", element: <SOSHealthScreen /> },
              { path: "sos/:orderId/tracking", element: <SOSTrackingScreen /> },
            ],
          },
        ],
      },

      // ── Espace agent ────────────────────────────────────────────────────
      {
        element: <RequireRole role="AGENT" />,
        children: [
          {
            path: "/agent",
            element: <AgentShell />,
            errorElement: <AppErrorBoundary />,
            children: [
              { index: true, element: <AgentHomeScreen /> },
              { path: "missions/:missionId", element: <AgentMissionScreen /> },
              { path: "missions/:missionId/checklist", element: <AgentChecklistScreen /> },
              { path: "missions/:missionId/encaissement", element: <AgentEncaissementScreen /> },
              { path: "trips/:tripId", element: <AgentTripScreen /> },
              { path: "trips/:tripId/stops/:stopIndex", element: <AgentVerifyStopScreen /> },
              {
                path: "trips/:tripId/stops/:stopIndex/pickup-proof",
                element: <AgentPickupProofScreen />,
              },
              { path: "gains", element: <AgentGainsScreen /> },
              { path: "gains/retrait", element: <AgentPayoutScreen /> },
              { path: "notifications", element: <AgentNotificationsScreen /> },
            ],
          },
        ],
      },
    ],
  },

  { path: "*", element: <NotFound /> },
]);
